import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

export const Terms: React.FC = () => {
  return (
    <div className="legal-container">

      <nav className="legal-topnav">
        <div className="legal-topnav-brand">
          <div className="legal-topnav-logo">W</div>
          <span className="legal-topnav-name">Wealth Financial Systems</span>
        </div>
        <div className="legal-topnav-links">
          <Link to="/privacy" className="legal-topnav-link">Privacy Policy</Link>
          <Link to="/" className="legal-topnav-link">Home</Link>
        </div>
      </nav>

      <div className="legal-content">
        <div className="legal-header">
          <h1>Terms and Conditions</h1>
          <p>Last updated: March 2, 2026</p>
          <p>Effective date: March 2, 2026</p>
        </div>

        {/* ── CRITICAL DISCLAIMER UP FRONT ── */}
        <div className="legal-highlight legal-highlight-red">
          <h3>⚠️ IMPORTANT — READ BEFORE USING THIS SERVICE</h3>
          <p>
            <strong>Wealth Financial Systems does not provide investment advice, financial advice,
            trading advice, or any other form of financial guidance.</strong> All content on this
            platform — including tools, calculators, signals, reports, articles, and AI-generated
            analysis — is for <strong>educational and informational purposes only</strong>.
          </p>
          <p style={{ marginTop: '0.75rem' }}>
            Nothing on this platform constitutes a recommendation to buy, sell, or hold any
            security, investment product, or financial instrument. You should always consult a
            qualified, licensed financial advisor before making any investment decisions.
          </p>
        </div>

        <div className="legal-section">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing, registering for, or using the Wealth Financial Systems platform
            ("Service," "Platform," or "we," "us," "our"), you agree to be bound by these
            Terms and Conditions ("Terms"). If you do not agree to these Terms in their
            entirety, you must not access or use this Service.
          </p>
          <p>
            These Terms apply to all users of the Service, including visitors, registered
            members, free-tier users, and paid subscribers. Your continued use of the Service
            following any modification to these Terms constitutes acceptance of those modifications.
          </p>
        </div>

        <div className="legal-section">
          <h2>2. Description of Service</h2>
          <p>
            Wealth Financial Systems is a financial education and data analysis platform that
            provides tools, content, and market data to help users learn about personal finance
            and investing. The Service includes, but is not limited to:
          </p>
          <ul>
            <li>Net Worth Tracker — personal asset and liability tracking</li>
            <li>Lifecycle Investment Planner — age-based investment education</li>
            <li>Wealth Playbook — educational content on wealth-building principles</li>
            <li>4-Bucket System Guide — educational content on retirement withdrawal strategy</li>
            <li>Bucket Visualizer — interactive tool for modelling retirement bucket allocation</li>
            <li>FIRE Investment Strategy Calculator — financial independence modelling tool</li>
            <li>Options Research — implied volatility rank data and pattern analysis</li>
            <li>Stock Research — technical and fundamental data indicators</li>
            <li>Stock Daily Reports — AI-generated market data summaries</li>
            <li>Email alerts and notifications based on market data thresholds</li>
            <li>Algo Trading Engine — automated stock trading via Alpaca based on technical scoring models</li>
            <li>Options Auto-Trader — automated credit spread trading via TastyTrade from V2 signal data</li>
            <li>Learn to Trade — educational feature showing real historical trade examples for learning purposes</li>
          </ul>
          <p>
            All tools and content are educational in nature. <strong>No output from any tool
            or report constitutes financial advice.</strong> We reserve the right to modify,
            suspend, or discontinue any feature of the Service at any time without notice.
          </p>
        </div>

        <div className="legal-section">
          <h2>3. Not Financial Advice — No Advisory Relationship</h2>
          <div className="legal-highlight">
            <h3>📋 Educational Content Only</h3>
            <p>
              Wealth Financial Systems is <strong>not a registered investment advisor,
              broker-dealer, financial planner, or financial institution</strong> of any kind.
              We are not licensed to provide investment advice in any jurisdiction.
            </p>
            <p style={{ marginTop: '0.75rem' }}>
              No content, tool output, signal, report, calculator result, or communication
              from Wealth Financial Systems or its employees, contractors, or affiliates
              creates or implies an advisory relationship. Use of the Service does not
              constitute a client–advisor relationship of any kind.
            </p>
            <p style={{ marginTop: '0.75rem' }}>
              <strong>You are solely responsible for all investment decisions you make.</strong>
              Always conduct your own due diligence and consult a qualified financial professional
              before acting on any information obtained through the Service.
            </p>
          </div>
        </div>

        <div className="legal-section">
          <h2>4. Risk Disclosure</h2>
          <p>
            Investing in financial markets involves substantial risk, including the possible
            loss of the entire amount invested. By using this Service, you acknowledge and
            accept the following:
          </p>
          <ul>
            <li>
              <strong>All investing carries risk.</strong> Past performance of any security,
              strategy, or market signal shown on this platform does not guarantee future results.
            </li>
            <li>
              <strong>Options and derivatives are high-risk instruments.</strong> Options trading
              can result in the total loss of premium paid and, in certain strategies, losses
              exceeding the initial investment. Options are not suitable for all investors.
            </li>
            <li>
              <strong>Market data may be delayed or inaccurate.</strong> Data displayed on this
              platform may not reflect real-time market conditions. We make no warranty regarding
              the accuracy, completeness, or timeliness of any market data.
            </li>
            <li>
              <strong>Calculators and projections are illustrative only.</strong> Output from
              FIRE calculators, bucket visualizers, and any other planning tools is based on
              assumptions that may not reflect actual market conditions or your personal situation.
            </li>
            <li>
              <strong>You may lose money.</strong> No strategy, signal, or educational content
              on this platform guarantees investment profit or protects against loss.
            </li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>5. AI-Generated Content Disclaimer</h2>
          <p>
            Portions of the content on this platform — including stock reports, market summaries,
            signal rationales, and analysis — are generated using artificial intelligence (AI)
            and automated systems. You acknowledge that:
          </p>
          <ul>
            <li>AI-generated content may contain errors, omissions, or inaccuracies</li>
            <li>AI-generated content does not reflect the judgment of a licensed financial professional</li>
            <li>AI outputs are not tailored to your individual financial situation, goals, or risk tolerance</li>
            <li>AI-generated content should never be the sole basis for any investment decision</li>
            <li>We do not guarantee the accuracy, completeness, or reliability of any AI-generated content</li>
          </ul>
          <p>
            AI-generated reports and signals are provided as informational summaries of publicly
            available data only. They do not constitute recommendations, and we expressly disclaim
            any liability arising from reliance on AI-generated content.
          </p>
        </div>

        <div className="legal-section">
          <h2>6. User Accounts</h2>
          <p>
            To access certain features of the Service, you must create an account. By registering,
            you agree to:
          </p>
          <ul>
            <li>Provide accurate, current, and complete information during registration and keep it updated</li>
            <li>Maintain the confidentiality of your password and account credentials</li>
            <li>Accept full responsibility for all activity occurring under your account</li>
            <li>Notify us immediately of any unauthorized use of your account</li>
            <li>Not create more than one account per person</li>
            <li>Not allow any other person to use your account</li>
          </ul>
          <p>
            We reserve the right to disable any account at any time, at our sole discretion,
            including for breach of these Terms or suspected fraudulent activity.
          </p>
        </div>

        <div className="legal-section">
          <h2>7. Subscriptions, Payments, and Refunds</h2>
          <p>
            Certain features of the Service are available only to paid subscribers ("Premier" tier).
            By subscribing, you agree to:
          </p>
          <ul>
            <li>Pay all applicable fees for the subscription tier you select</li>
            <li>Authorize recurring charges to your payment method on the billing cycle selected</li>
            <li>Keep your payment information current and accurate</li>
          </ul>
          <p>
            <strong>Refund Policy:</strong> Subscription fees are generally non-refundable except
            where required by applicable law. We may, at our sole discretion, offer a refund or
            service credit in exceptional circumstances. Contact us within 7 days of a charge
            if you believe an error has occurred.
          </p>
          <p>
            We reserve the right to change subscription pricing at any time. You will be notified
            of price changes at least 30 days before they take effect for existing subscribers.
          </p>
        </div>

        <div className="legal-section">
          <h2>8. User Conduct</h2>
          <p>You agree not to use the Service to:</p>
          <ul>
            <li>Violate any applicable local, state, national, or international law or regulation</li>
            <li>Engage in market manipulation or any activity that violates securities laws</li>
            <li>Transmit any harmful, malicious, or disruptive code or software</li>
            <li>Attempt to gain unauthorized access to any part of the Service, its servers, or systems</li>
            <li>Share, sell, or distribute your account credentials or any premium content to third parties</li>
            <li>Scrape, reproduce, or redistribute any content, signals, or data from the Service</li>
            <li>Use the Service for any commercial purpose without our express written authorization</li>
            <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>9. Intellectual Property</h2>
          <p>
            The Service and all of its content, features, tools, and functionality — including
            but not limited to text, graphics, logos, software, calculations, signal algorithms,
            and the 4-Bucket System educational framework — are the exclusive property of
            Wealth Financial Systems and its licensors, and are protected by copyright, trademark,
            trade secret, and other intellectual property laws.
          </p>
          <p>
            You are granted a limited, non-exclusive, non-transferable, revocable licence to
            access and use the Service for your own personal, non-commercial purposes only.
            This licence does not permit you to reproduce, distribute, modify, create derivative
            works from, or commercially exploit any content from the Service.
          </p>
        </div>

        <div className="legal-section">
          <h2>10. Data and Privacy</h2>
          <p>
            Your privacy is important to us. Your use of the Service is also governed by our
            <Link to="/privacy"> Privacy Policy</Link>, which is incorporated into these Terms
            by reference. By using the Service, you consent to the collection and use of your
            information as described in the Privacy Policy.
          </p>
        </div>

        <div className="legal-section">
          <h2>11. Service Availability and Data Accuracy</h2>
          <p>
            We strive to provide a reliable service, but we make no guarantees or warranties,
            express or implied, regarding:
          </p>
          <ul>
            <li>Continuous, uninterrupted, or error-free availability of the Service</li>
            <li>The accuracy, completeness, timeliness, or reliability of any market data, signals, or reports</li>
            <li>The delivery or timeliness of email alerts or notifications</li>
            <li>The performance or suitability of any investment strategy described or modelled on the platform</li>
          </ul>
          <p>
            Market data is sourced from third-party providers. We are not responsible for
            errors or omissions in third-party data. The Service may be temporarily unavailable
            due to maintenance, updates, or circumstances beyond our control.
          </p>
        </div>

        <div className="legal-section">
          <h2>12. Disclaimer of Warranties</h2>
          <p>
            <strong>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTY OF
            ANY KIND, EXPRESS OR IMPLIED.</strong> To the fullest extent permitted by applicable law,
            Wealth Financial Systems expressly disclaims all warranties, including but not limited to:
          </p>
          <ul>
            <li>Implied warranties of merchantability, fitness for a particular purpose, and non-infringement</li>
            <li>Any warranty that the Service will meet your requirements or expectations</li>
            <li>Any warranty regarding the accuracy, reliability, or completeness of any information provided</li>
            <li>Any warranty that defects will be corrected or that the Service is free of viruses or harmful components</li>
            <li>Any warranty that investment strategies discussed or illustrated will produce any particular result</li>
          </ul>
          <p>
            No advice or information, whether oral or written, obtained from Wealth Financial Systems
            or through the Service shall create any warranty not expressly stated in these Terms.
          </p>
        </div>

        <div className="legal-section">
          <h2>13. Limitation of Liability</h2>
          <div className="legal-highlight">
            <h3>⚠️ Critical Limitation — Please Read Carefully</h3>
            <p>
              <strong>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL
              WEALTH FINANCIAL SYSTEM, ITS OFFICERS, DIRECTORS, EMPLOYEES, CONTRACTORS,
              AGENTS, AFFILIATES, PARTNERS, OR LICENSORS BE LIABLE TO YOU OR ANY THIRD PARTY
              FOR ANY:</strong>
            </p>
          </div>
          <ul>
            <li>
              <strong>Investment or trading losses</strong> — including losses arising from
              decisions made based on any signal, report, calculator output, AI-generated content,
              or any other information obtained through the Service
            </li>
            <li>
              <strong>Lost profits or revenue</strong> — whether direct or indirect, including
              missed trading opportunities or foregone investment gains
            </li>
            <li>
              <strong>Consequential, incidental, special, punitive, or exemplary damages</strong> —
              regardless of whether we have been advised of the possibility of such damages
            </li>
            <li>
              <strong>Data loss or corruption</strong> — including loss of your account data,
              financial records, or any other information
            </li>
            <li>
              <strong>Errors in market data or signals</strong> — including inaccurate price data,
              delayed data, or incorrect signal generation
            </li>
            <li>
              <strong>Service interruptions</strong> — including unavailability of the platform
              during critical market hours or events
            </li>
            <li>
              <strong>Third-party actions</strong> — including unauthorized access to your account
              or data by third parties despite our security measures
            </li>
            <li>
              <strong>Reliance on AI-generated content</strong> — including any inaccuracies,
              errors, or misleading information in automated reports or analysis
            </li>
          </ul>
          <p>
            <strong>IN NO EVENT SHALL OUR TOTAL CUMULATIVE LIABILITY TO YOU FOR ANY CLAIMS
            ARISING FROM OR RELATING TO THE SERVICE EXCEED THE GREATER OF: (A) THE TOTAL AMOUNT
            YOU PAID TO US IN THE 12 MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO
            THE CLAIM, OR (B) ONE HUNDRED DOLLARS ($100.00 USD).</strong>
          </p>
          <p>
            Some jurisdictions do not allow the exclusion or limitation of certain types of
            damages. In such jurisdictions, our liability shall be limited to the maximum extent
            permitted by law.
          </p>
        </div>

        <div className="legal-section">
          <h2>14. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Wealth Financial Systems and its
            officers, directors, employees, contractors, agents, affiliates, and successors from
            and against any and all claims, damages, losses, liabilities, costs, and expenses
            (including reasonable attorneys' fees) arising out of or relating to:
          </p>
          <ul>
            <li>Your use of or access to the Service</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any applicable law or regulation, including securities laws</li>
            <li>Any investment or financial decision you make based on information from the Service</li>
            <li>Your infringement of any third-party intellectual property or other rights</li>
            <li>Any content you submit, post, or otherwise make available through the Service</li>
          </ul>
          <p>
            We reserve the right, at our own expense, to assume the exclusive defence and control
            of any matter subject to indemnification by you, in which case you agree to cooperate
            fully with our defence.
          </p>
        </div>

        <div className="legal-section">
          <h2>15. Termination</h2>
          <p>
            We may terminate or suspend your access to the Service immediately, without prior
            notice or liability, at our sole discretion, for any reason, including without
            limitation if you breach these Terms. Upon termination:
          </p>
          <ul>
            <li>Your right to use the Service will immediately cease</li>
            <li>Any outstanding subscription fees remain due and payable</li>
            <li>We may delete your account data, subject to our Privacy Policy</li>
            <li>Provisions of these Terms that by their nature should survive termination shall survive</li>
          </ul>
          <p>
            You may terminate your account at any time by contacting us. Termination does not
            entitle you to a refund of any prepaid subscription fees, except as required by law.
          </p>
        </div>

        <div className="legal-section">
          <h2>16. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. We will notify you of
            material changes by posting the updated Terms on this page and updating the
            "Last updated" date. For significant changes, we will also notify you by email
            at least 30 days before the changes take effect.
          </p>
          <p>
            Your continued use of the Service after changes take effect constitutes your
            acceptance of the revised Terms. If you do not agree to the new Terms, you must
            stop using the Service before the effective date of the changes.
          </p>
        </div>

        <div className="legal-section">
          <h2>17. Governing Law and Dispute Resolution</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the
            United States, without regard to its conflict of law provisions. Any dispute arising
            from or relating to these Terms or the Service shall be resolved through binding
            arbitration in accordance with the rules of the American Arbitration Association,
            rather than in court, except that either party may seek injunctive or other equitable
            relief in any court of competent jurisdiction.
          </p>
          <p>
            <strong>Class Action Waiver:</strong> You agree that any dispute resolution proceedings
            will be conducted only on an individual basis and not in a class, consolidated, or
            representative action. You waive any right to participate in a class action lawsuit
            or class-wide arbitration.
          </p>
        </div>

        <div className="legal-section">
          <h2>18. Severability and Entire Agreement</h2>
          <p>
            If any provision of these Terms is held to be invalid, illegal, or unenforceable,
            the remaining provisions shall continue in full force and effect. These Terms, together
            with our Privacy Policy, constitute the entire agreement between you and Wealth
            Financial System with respect to your use of the Service, and supersede all prior
            agreements, representations, and understandings.
          </p>
        </div>

        <div className="legal-section" id="trade-with-me">
          <h2>19. Learn to Trade — Educational Trade Examples</h2>

          <h3>19.1 Description</h3>
          <p>
            The "Learn to Trade" feature provides eligible subscribers with access to real
            historical trade examples executed by Wealth Financial Systems LLC ("Wealth Financial Systems") or its
            designated educators. These examples are provided solely for educational and
            training purposes to help subscribers learn about trading strategies, risk
            management, and market decision-making. Wealth Financial Systems does not have access to,
            custody of, or control over any subscriber's brokerage account or funds.
          </p>

          <h3>19.2 Not Investment Advice</h3>
          <p>
            The Learn to Trade feature is provided solely for informational and educational purposes.
            It is not, nor is it intended to be, trading or investment advice or a recommendation
            that any security, futures contract, options contract, digital asset, other product,
            transaction, or investment strategy is suitable for any person.
          </p>
          <p>
            Wealth Financial Systems is not a licensed financial advisor, registered investment advisor, or a
            registered broker-dealer. Wealth Financial Systems is not in the business of transacting securities
            trades, nor does it direct client accounts or give trading advice tailored to any
            particular client's situation. Nothing contained in the Learn to Trade feature
            constitutes a solicitation, recommendation, promotion, or endorsement of any
            particular security, other investment product, transaction, or investment.
          </p>

          <h3>19.3 Assumption of Risk</h3>
          <p>
            Trading securities, options, futures products, and digital assets involves substantial
            risk and may result in a loss greater than the original amount invested. The trade
            examples shown are historical and for educational purposes only. By accessing the
            Learn to Trade feature, you acknowledge and agree that:
          </p>
          <ul>
            <li>
              The trade examples shown are historical records of completed trades. They are
              not recommendations, signals, or instructions to place any trade.
            </li>
            <li>
              If you choose to place trades based on what you learn, you are assuming the entire
              risk. Your results will differ due to timing, account size, buying power,
              commissions, and market conditions.
            </li>
            <li>
              Past performance shown in trade examples is not indicative of future results, and
              no representation is made that you will achieve similar profits or losses.
            </li>
            <li>
              The Learn to Trade feature does not take into account your investment objectives,
              financial situation, or needs. You are solely responsible for making your own
              investment and trading decisions.
            </li>
            <li>
              You should consult a qualified financial advisor before making any investment
              decisions. Wealth Financial Systems does not provide personalized investment advice.
            </li>
          </ul>

          <h3>19.4 Ownership Disclosure</h3>
          <p>
            Employees, contractors, owners, and others connected with Wealth Financial Systems LLC may own
            positions in the equities, options, securities, and/or futures positions mentioned on
            the Platform, displayed through the Learn to Trade feature, or discussed in our content.
            The trade examples shown reflect real positions held by Wealth Financial Systems or its
            educators. They may continue to hold positions in these instruments.
          </p>

          <h3>19.5 No Guarantee of Results</h3>
          <p>
            The trade examples are provided as-is for educational study. Wealth Financial Systems does not
            guarantee that similar trades will produce similar results. Market conditions,
            timing, liquidity, and individual circumstances vary. Wealth Financial Systems shall not be
            liable for any losses arising from trades you choose to place based on these
            educational examples.
          </p>

          <h3>19.6 Acceptance Required</h3>
          <p>
            Before accessing the Learn to Trade feature, you will be required to review and accept
            a disclaimer acknowledging the risks described in this section and confirming that
            you understand the content is educational, not investment advice. You may cancel
            your subscription at any time through your account settings.
          </p>
        </div>

        <div className="legal-section">
          <h2>20. Contact Information</h2>
          <p>
            If you have any questions about these Terms and Conditions, please contact us:
          </p>
          <div className="legal-contact">
            <p>
              <strong>Wealth Financial Systems</strong><br />
              <strong>Email:</strong> support@wealthfinancialsystems.com<br />
              <strong>Subject line:</strong> Terms and Conditions Inquiry<br />
              <strong>Response time:</strong> We aim to respond within 3–5 business days.
            </p>
          </div>
        </div>

        <div className="legal-footer">
          <p>
            <strong>By using Wealth Financial Systems, you acknowledge that you have read,
            understood, and agree to be bound by these Terms and Conditions, including
            the investment risk disclosures and limitations of liability.</strong>
          </p>
          <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#6c757d' }}>
            This platform is for educational purposes only. Wealth Financial Systems is not a
            registered investment adviser, broker-dealer, or financial institution.
            Nothing here constitutes investment advice.
          </p>
        </div>
      </div>
    </div>
  );
};
